import { initializeApp, getApps, getApp, FirebaseApp } from 'firebase/app';
import {
  getFirestore,
  connectFirestoreEmulator,
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  onSnapshot,
  Firestore,
  Unsubscribe,
} from 'firebase/firestore';
import { Company, PolicyRevision } from '../types';
import { cleanPolicyTitle } from '../utils/policyUtils';

// Public Firebase configuration for tostracker-masiosare
const firebaseConfig = {
  apiKey: (typeof import.meta !== 'undefined' && import.meta.env?.VITE_FIREBASE_API_KEY) || "AIzaSyCZ3DWO6jlmHLn__wUjDWwMM2dheHy9KTQ",
  authDomain: (typeof import.meta !== 'undefined' && import.meta.env?.VITE_FIREBASE_AUTH_DOMAIN) || "tostracker-masiosare.firebaseapp.com",
  projectId: (typeof import.meta !== 'undefined' && import.meta.env?.VITE_FIREBASE_PROJECT_ID) || "tostracker-masiosare",
  storageBucket: (typeof import.meta !== 'undefined' && import.meta.env?.VITE_FIREBASE_STORAGE_BUCKET) || "tostracker-masiosare.firebasestorage.app",
  messagingSenderId: (typeof import.meta !== 'undefined' && import.meta.env?.VITE_FIREBASE_MESSAGING_SENDER_ID) || "846477991978",
  appId: (typeof import.meta !== 'undefined' && import.meta.env?.VITE_FIREBASE_APP_ID) || "1:846477991978:web:7b55ed59d5d2f460412353",
  measurementId: (typeof import.meta !== 'undefined' && import.meta.env?.VITE_FIREBASE_MEASUREMENT_ID) || "G-275P6YQ2E4",
};

// Initialize Firebase App & Firestore
let app: FirebaseApp;
let db: Firestore;

try {
  app = getApps().length ? getApp() : initializeApp(firebaseConfig);
  db = getFirestore(app);

  const isLocalhost =
    typeof window !== 'undefined' &&
    window.location.hostname === 'localhost';

  // Only connect to emulator if explicitly instructed by VITE_USE_FIREBASE_EMULATOR=true
  if (isLocalhost && typeof import.meta !== 'undefined' && import.meta.env?.VITE_USE_FIREBASE_EMULATOR === 'true') {
    try {
      connectFirestoreEmulator(db, 'localhost', 8080);
      console.info('[Firestore] Connected to local Firestore Emulator at localhost:8080');
    } catch (err: any) {
      console.debug('[Firestore] Emulator notice:', err.message);
    }
  }
} catch (e: any) {
  console.warn('[Firestore] Initialization warning:', e.message);
}

export { db, app };

// -------------------------------------------------------------
// Data Normalizers
// -------------------------------------------------------------

function normalizeCompany(id: string, data: any): Company {
  return {
    id,
    name: data.name || id,
    domain: data.domain || `${id}.com`,
    logoUrl: data.logo_url || data.logoUrl || `https://${data.domain || id}.com/favicon.ico`,
    logo_url: data.logo_url || data.logoUrl,
    description: data.description || '',
    category: data.category || 'AI / Tech',
    status: data.status || 'active',
    totalRevisions: data.total_revisions ?? data.totalRevisions ?? 0,
    total_revisions: data.total_revisions ?? data.totalRevisions ?? 0,
    trackedPolicies: data.tracked_policies || data.trackedPolicies || {},
    tracked_policies: data.tracked_policies || data.trackedPolicies || {},
    createdAt: data.created_at || data.createdAt,
    created_at: data.created_at || data.createdAt,
    updatedAt: data.updated_at || data.updatedAt,
    updated_at: data.updated_at || data.updatedAt,
    lastScrapedAt: data.last_scraped_at || data.lastScrapedAt,
    last_scraped_at: data.last_scraped_at || data.lastScrapedAt,
  };
}

function normalizeRevision(id: string, data: any): PolicyRevision {
  return {
    id,
    companyId: data.company_id || data.companyId || '',
    company_id: data.company_id || data.companyId || '',
    policyType: data.policy_type || data.policyType || 'terms_of_service',
    policy_type: data.policy_type || data.policyType || 'terms_of_service',
    scrapedAt: data.scraped_at || data.scrapedAt || new Date().toISOString(),
    scraped_at: data.scraped_at || data.scrapedAt || new Date().toISOString(),
    effectiveDate: data.effective_date || data.effectiveDate,
    effective_date: data.effective_date || data.effectiveDate,
    title: data.title || `Revision ${id}`,
    wordCount: data.word_count ?? data.wordCount ?? 0,
    word_count: data.word_count ?? data.wordCount ?? 0,
    characterCount: data.character_count ?? data.characterCount ?? 0,
    character_count: data.character_count ?? data.characterCount ?? 0,
    sha256Hash: data.sha256_hash || data.sha256Hash || '',
    sha256_hash: data.sha256_hash || data.sha256Hash || '',
    rawText: data.raw_text || data.rawText || '',
    raw_text: data.raw_text || data.rawText || '',
    storageUri: data.storage_uri || data.storageUri,
    storage_uri: data.storage_uri || data.storageUri,
    textExcerpt: data.text_excerpt || data.textExcerpt,
    text_excerpt: data.text_excerpt || data.textExcerpt,
    executiveSummary: data.executive_summary || data.executiveSummary,
    executive_summary: data.executive_summary || data.executiveSummary,
    keyTakeaways: data.key_takeaways || data.keyTakeaways || [],
    key_takeaways: data.key_takeaways || data.keyTakeaways || [],
    diffSummary: data.diff_summary || data.diffSummary,
    diff_summary: data.diff_summary || data.diffSummary,
    structuredDiff: data.structured_diff || data.structuredDiff,
    structured_diff: data.structured_diff || data.structuredDiff,
    previousRevisionId: data.previous_revision_id || data.previousRevisionId,
    previous_revision_id: data.previous_revision_id || data.previousRevisionId,
    metadata: data.metadata || {},
  };
}

// -------------------------------------------------------------
// Real-Time Subscriptions (Live Data Only)
// -------------------------------------------------------------

/**
 * Subscribes to live updates on the companies collection.
 */
export function subscribeCompanies(
  onUpdate: (companies: Company[]) => void,
  onError?: (err: Error) => void
): Unsubscribe {
  try {
    if (!db) {
      onUpdate([]);
      return () => {};
    }
    const companiesCol = collection(db, 'companies');

    return onSnapshot(
      companiesCol,
      (snapshot) => {
        const companies: Company[] = [];
        snapshot.forEach((d) => {
          companies.push(normalizeCompany(d.id, d.data()));
        });
        companies.sort((a, b) => a.name.localeCompare(b.name));
        onUpdate(companies);
      },
      (err) => {
        console.warn('[Firestore] Realtime companies listener error:', err.message);
        if (onError) onError(err);
      }
    );
  } catch (e: any) {
    console.warn('[Firestore] Failed to establish companies listener:', e.message);
    onUpdate([]);
    return () => {};
  }
}

/**
 * Subscribes to chronological revisions for a specific company and policy.
 */
export function subscribeRevisions(
  companyId: string,
  policyType: string,
  onUpdate: (revisions: PolicyRevision[]) => void,
  onError?: (err: Error) => void
): Unsubscribe {
  try {
    if (!db) {
      onUpdate([]);
      return () => {};
    }
    const revisionsCol = collection(db, 'companies', companyId, 'policies', policyType, 'revisions');

    return onSnapshot(
      revisionsCol,
      (snapshot) => {
        const revisions: PolicyRevision[] = [];
        snapshot.forEach((d) => {
          revisions.push(normalizeRevision(d.id, d.data()));
        });
        // Sort descending by scraped timestamp
        revisions.sort((a, b) => {
          const tA = new Date(a.scraped_at || a.scrapedAt || 0).getTime();
          const tB = new Date(b.scraped_at || b.scrapedAt || 0).getTime();
          return tB - tA;
        });
        onUpdate(revisions);
      },
      (err) => {
        console.warn(`[Firestore] Revisions query failed for ${companyId}/${policyType}:`, err.message);
        onUpdate([]);
        if (onError) onError(err);
      }
    );
  } catch (e: any) {
    onUpdate([]);
    return () => {};
  }
}

/**
 * Fetches a single company document.
 */
export async function fetchCompanyDoc(companyId: string): Promise<Company | null> {
  try {
    if (!db) return null;
    const docRef = doc(db, 'companies', companyId);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      return normalizeCompany(snap.id, snap.data());
    }
  } catch (err: any) {
    console.warn('[Firestore] fetchCompanyDoc failed:', err.message);
  }
  return null;
}

/**
 * Fetches a single revision document.
 */
export async function fetchRevisionDoc(
  companyId: string,
  policyType: string,
  revisionId: string
): Promise<PolicyRevision | null> {
  try {
    if (!db) return null;
    const docRef = doc(db, 'companies', companyId, 'policies', policyType, 'revisions', revisionId);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      return normalizeRevision(snap.id, snap.data());
    }
  } catch (err: any) {
    console.warn('[Firestore] fetchRevisionDoc failed:', err.message);
  }
  return null;
}

/**
 * Fetches a diff document from /diffs/{diffId}.
 */
export async function fetchDiffDoc(diffId: string): Promise<any | null> {
  try {
    if (!db) return null;
    const docRef = doc(db, 'diffs', diffId);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      return snap.data();
    }
  } catch (err: any) {
    console.warn('[Firestore] fetchDiffDoc failed:', err.message);
  }
  return null;
}

/**
 * Fetches recent revisions across all companies and policies to build a live activity feed.
 */
export async function fetchRecentActivityFeed(companies: Company[]): Promise<import('../types').ActivityFeedItem[]> {
  if (!db || companies.length === 0) return [];
  const feedItems: import('../types').ActivityFeedItem[] = [];

  const promises = companies.flatMap((company) => {
    const tracked = (company as any).trackedPolicies || (company as any).tracked_policies || {};
    const policyTypes = Object.keys(tracked).length > 0
      ? Object.keys(tracked)
      : ['terms_of_service', 'privacy_policy'];

    return policyTypes.map(async (policyType) => {
      try {
        const revCol = collection(db, 'companies', company.id, 'policies', policyType, 'revisions');
        const snap = await getDocs(revCol);
        if (snap.empty) return;

        const revs: PolicyRevision[] = [];
        snapshotToRevisions(snap, revs);
        revs.sort((a, b) => {
          const tA = new Date(a.scraped_at || a.scrapedAt || 0).getTime();
          const tB = new Date(b.scraped_at || b.scrapedAt || 0).getTime();
          return tB - tA;
        });

        const cfg = tracked[policyType];
        const cleanName = cleanPolicyTitle(cfg?.name, policyType);

        // Top 2 revisions per policy
        revs.slice(0, 2).forEach((rev, idx) => {
          const prevRev = idx < revs.length - 1 ? revs[idx + 1] : undefined;
          const analysis = rev.diffAnalysis || rev.diff_summary || rev.summary;
          const score = analysis?.significanceScore ?? analysis?.significance_score ?? 0;
          const sigLevel = analysis?.significance;
          const isBaseline = !prevRev && (!rev.previous_revision_id || rev.previous_revision_id === 'initial');

          feedItems.push({
            id: `feed_${company.id}_${policyType}_${rev.id}`,
            companyId: company.id,
            companyName: company.name,
            companyDomain: company.domain,
            companyLogo: company.logoUrl || company.logo_url,
            companyCategory: company.category,
            policyType,
            policyName: cleanName,
            revisionId: rev.id,
            previousRevisionId: prevRev?.id || rev.previous_revision_id || null,
            timestamp: rev.scraped_at || rev.scrapedAt || company.updatedAt || new Date().toISOString(),
            title: cleanPolicyTitle(rev.title, policyType),
            headline: analysis?.headline || analysis?.change_headline || (isBaseline ? 'Initial baseline document snapshot recorded.' : 'Policy update detected.'),
            keyTakeaways: analysis?.keyChanges || analysis?.key_changes || (rev.key_takeaways || rev.keyTakeaways || []),
            significanceScore: score,
            significanceLevel: sigLevel,
            wordCount: rev.word_count || rev.wordCount,
            isBaseline,
          });
        });
      } catch (err: any) {
        console.debug(`[Firestore] Feed query skipped for ${company.id}/${policyType}:`, err.message);
      }
    });
  });

  await Promise.allSettled(promises);

  feedItems.sort((a, b) => {
    return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
  });

  return feedItems;
}

function snapshotToRevisions(snap: any, out: PolicyRevision[]) {
  snap.forEach((d: any) => {
    out.push(normalizeRevision(d.id, d.data()));
  });
}
